import {
  FILTER_KIND_INPUT,
  FILTER_KIND_OUTPUT,
  INPUT_COLUMN_FILTER_CHANGE_TYPE,
  INPUT_DATE_FILTER_CHANGE_TYPE,
  INPUT_NUMBER_FILTER_CHANGE_TYPE,
} from "../types/constants";
import { getFilterTypeFromResultColumn } from "../types/filterTypes";
import {
  detectAllFilterChanges,
  detectColumnChanges,
  detectColumnDecimalsChanges,
  detectColumnWidthChanges,
  detectGroupByChanges,
  detectMetricsChanges,
  detectRenameChanges,
  detectSearchChanges,
  detectSortChanges,
  detectSummaryChanges,
} from "../utils/changeDetectors";
import { carryOverConfigurationState } from "../utils/configurationUtils";
import { isDateRangeSet } from "../utils/dateUtils";
import {
  activeFilterSpecs,
  columnFilterNumberFormatter,
  filterKindForChangeType,
  filterKindLabels,
  selectedValuesChipLabel,
} from "../utils/filterKindUtils";
import {
  deriveFilterMaps,
  formatDateInput,
  parseDateInput,
} from "../utils/filterSpecUtils";
import {
  buildGroupingColumnName,
  filterDisplayColumn,
  mapGroupingOrderToSource,
  withoutOrphanedGroupingFilters,
  rangeGroupingsFromAggregate,
} from "../utils/rangeGroupingUtils";
import {
  buildBooleanFilter,
  buildDateFilter,
  buildNumberFilter,
  buildValueFilter,
  createEmptySpecification,
  toFrontendFilters,
} from "../utils/reportStateUtils";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const ReportStateContext = createContext(null);

export const ReportStateProvider = ({
  children,
  reportId,
  savedReport,
  sourceTables,
  columnDisplayNameMap,
  getColumnDataType,
  initialColumnsData,
  onReorderColumns,
  selectedResultIndex = 0,
}) => {
  const value = useReportState({
    reportId,
    savedReport,
    sourceTables,
    columnDisplayNameMap,
    getColumnDataType,
    initialColumnsData,
    onReorderColumns,
    selectedResultIndex,
  });

  return (
    <ReportStateContext.Provider value={value}>
      {children}
    </ReportStateContext.Provider>
  );
};

export const useReportStateContext = () => {
  const ctx = useContext(ReportStateContext);
  if (!ctx) {
    throw new Error(
      "useReportStateContext must be used within a ReportStateProvider"
    );
  }
  return ctx;
};

// ---------------------------------------------------------------------------
// Pure helpers (defined outside the hook so they can be used in useState
// initializers and callbacks without being recreated on every render)
// ---------------------------------------------------------------------------

export const apiOrderToSortModel = (order) => {
  if (!order || order.length === 0) return [];
  return order.map((o) => ({
    field: o.column,
    sort: o.direction === "DESC" ? "desc" : "asc",
  }));
};

export const deriveVisibilityFromResultColumns = (columnsData, spec) => {
  const specVisMap = {};
  (spec?.outputColumns ?? []).forEach((c) => {
    specVisMap[c.column] = !c.notForExport;
  });
  const model = {};
  (columnsData ?? []).forEach((c) => {
    model[c.column] = Object.prototype.hasOwnProperty.call(specVisMap, c.column)
      ? specVisMap[c.column]
      : !c.notForExport;
  });
  return model;
};

export const buildGroupingFromSpec = (spec) => {
  if (!spec?.group?.[0]) {
    return {
      groupByColumns: new Set(),
      columnAggregations: {},
      extraAggregateColumns: [],
      includeDetails: false,
      includeTotal: false,
      includeSubTotal: false,
      allValuesEnumerations: new Set(),
    };
  }
  const groupEntry = spec.group[0];
  const groupByColumns = new Set(groupEntry.groupColumns ?? []);
  const columnAggregations = {};
  const extraAggregateColumns = [];
  (groupEntry.aggregate ?? []).forEach((entry) => {
    if (entry.column && entry.expression?.func) {
      if (entry.expression.sourceColumn !== entry.column) {
        extraAggregateColumns.push({
          column: entry.column,
          func: entry.expression.func,
          sourceColumn: entry.expression.sourceColumn,
        });
      } else {
        columnAggregations[entry.column] = entry.expression.func;
      }
    }
  });
  return {
    groupByColumns,
    columnAggregations,
    extraAggregateColumns,
    includeDetails: groupEntry.includeDetails ?? false,
    includeTotal: groupEntry.includeTotal ?? false,
    includeSubTotal: groupEntry.includeSubTotal ?? false,
    allValuesEnumerations: new Set(groupEntry.allValuesEnumerations ?? []),
  };
};

export const buildSummaryFromSpec = (spec) => {
  const aggregations = spec?.summaryRow?.aggregations;
  if (!aggregations || aggregations.length === 0) {
    return { summarySelections: {}, summaryType: "BOTTOM" };
  }
  const summarySelections = {};
  aggregations.forEach((entry) => {
    if (!entry?.column) return;
    if (entry.override !== undefined && entry.override !== null) {
      summarySelections[entry.column] = { override: entry.override };
    } else if (entry.expression?.func) {
      summarySelections[entry.column] = { func: entry.expression.func };
    }
  });
  return {
    summarySelections,
    summaryType: spec.summaryRow.position ?? "BOTTOM",
  };
};

/**
 * Build a full TabEntry from a specification + report configuration.
 * Working state and saved state are initialised to the same values so
 * there are no false positives in change detection on first render.
 */
export const buildTabEntry = (
  spec,
  reportConfig,
  sourceTables,
  staleColumnKeys = []
) => {
  const resolvedSpec =
    spec ?? createEmptySpecification(sourceTables ?? ["review_list"]);

  const {
    filters: apiFilters,
    preAggregationFilters: apiInputFilters,
    ...baseSpecWithoutFilters
  } = resolvedSpec;

  const searchFilter = (apiFilters ?? []).find((f) => f.function === "SEARCH");
  const nonSearchFilters = (apiFilters ?? []).filter(
    (f) => f.function !== "SEARCH"
  );

  const sortModel = apiOrderToSortModel(
    mapGroupingOrderToSource(resolvedSpec.order ?? [])
  );
  const columnVisibilityModel = {};
  const columnOrder = (resolvedSpec.outputColumns ?? []).map((oc) => oc.column);
  const pinnedColumns = (resolvedSpec.outputColumns ?? [])
    .filter((oc) => oc.pinned)
    .map((oc) => oc.column);
  const columnDecimals = Object.fromEntries(
    (resolvedSpec.outputColumns ?? [])
      .filter((oc) => oc.decimals !== undefined && oc.decimals !== null)
      .map((oc) => [oc.column, oc.decimals])
  );
  const {
    groupByColumns,
    columnAggregations,
    extraAggregateColumns,
    includeDetails,
    includeTotal,
    includeSubTotal,
    allValuesEnumerations,
  } = buildGroupingFromSpec(resolvedSpec);
  const rangeGroupings = rangeGroupingsFromAggregate(resolvedSpec.aggregate);
  const { summarySelections, summaryType } = buildSummaryFromSpec(resolvedSpec);
  const searchQuery = searchFilter?.parameters ?? "";
  const metricsItems = reportConfig?.metrics ?? [];
  const frontendFilters = toFrontendFilters(nonSearchFilters, "saved");

  return {
    // Working state
    filters: frontendFilters,
    preAggregationFilters: toFrontendFilters(apiInputFilters ?? [], "saved"),
    sortModel,
    columnVisibilityModel,
    columnOrder,
    pinnedColumns,
    columnWidths: {},
    columnDecimals,
    metricsItems,
    columnNameOverrides: {},
    groupByColumns,
    columnAggregations,
    extraAggregateColumns,
    removedExtraAggregateColumns: new Set(),
    rangeGroupings,
    searchQuery,
    includeDetails,
    includeTotal,
    includeSubTotal,
    allValuesEnumerations,
    summarySelections,
    summaryType,
    baseSpecification: baseSpecWithoutFilters,
    // Saved baselines. When columns were pruned from a saved view (they no longer
    // exist), seed the saved column order WITH those pruned keys so the loaded
    // view reads as having unsaved changes — the "•" shows and Update is enabled,
    // letting the user persist the cleaned view (and stop the stale-column toast).
    savedSortModel: sortModel,
    savedColumnVisibility: columnVisibilityModel,
    savedColumnOrder: staleColumnKeys.length
      ? [...columnOrder, ...staleColumnKeys]
      : columnOrder,
    savedPinnedColumns: pinnedColumns,
    savedColumnWidths: {},
    savedColumnDecimals: { ...columnDecimals },
    savedMetricsItems: metricsItems,
    savedSearchQuery: searchQuery,
    savedGroupByColumns: new Set(groupByColumns),
    savedColumnAggregations: { ...columnAggregations },
    savedExtraAggregateColumns: [...extraAggregateColumns],
    savedRangeGroupings: { ...rangeGroupings },
    savedIncludeDetails: includeDetails,
    savedIncludeTotal: includeTotal,
    savedIncludeSubTotal: includeSubTotal,
    savedAllValuesEnumerations: new Set(allValuesEnumerations),
    savedSummarySelections: { ...summarySelections },
    savedSummaryType: summaryType,
    savedSpecification: resolvedSpec,
  };
};

// Fingerprint EVERYTHING that defines a view, not just its columns. Switching
// between saved views (same reportId) only re-inits working state when this
// changes — so omitting filters/order/summaryRow/metrics means two views with
// identical columns but different sort/filters/metrics wouldn't refresh.
export const computeReportFingerprint = (savedReport) => {
  if (!savedReport) return null;
  const fingerprintSpec = (spec) =>
    JSON.stringify({
      outputColumns: spec?.outputColumns,
      group: spec?.group,
      sourceColumns: spec?.sourceColumns,
      aggregate: spec?.aggregate,
      filters: spec?.filters,
      preAggregationFilters: spec?.preAggregationFilters,
      order: spec?.order,
      summaryRow: spec?.summaryRow,
    });
  const metricsPart = JSON.stringify(
    savedReport.configuration?.metrics ?? null
  );
  if (savedReport.reports) {
    return (
      savedReport.reports
        .map((r) => fingerprintSpec(r.specification))
        .join("|") +
      "::" +
      metricsPart
    );
  }
  return fingerprintSpec(savedReport.specification) + "::" + metricsPart;
};

/**
 * Build the initial Map<tabIndex, TabEntry> for the given savedReport.
 */
export const buildAllTabEntries = (savedReport, sourceTables) => {
  const map = new Map();
  if (!savedReport) return map;

  if (savedReport.reports) {
    const outputReports = (savedReport.reports ?? []).filter(
      (r) => !r.notForOutput
    );
    outputReports.forEach((report, idx) => {
      map.set(
        idx,
        buildTabEntry(
          report.specification ?? null,
          savedReport.configuration ?? null,
          sourceTables
        )
      );
    });
    return map;
  }

  if (savedReport.specification) {
    map.set(
      0,
      buildTabEntry(
        savedReport.specification,
        savedReport.configuration ?? null,
        sourceTables,
        savedReport.staleColumnKeys ?? []
      )
    );
  }

  return map;
};

// ---------------------------------------------------------------------------
// Computes pending changes from a TabEntry (used in tab-switch effect)
// ---------------------------------------------------------------------------

export const deriveFilterMapsFromSpec = (filters, initialColumnsData) =>
  deriveFilterMaps(filters, (column) => {
    const colDef = initialColumnsData?.find((c) => c.column === column);
    return getFilterTypeFromResultColumn(colDef?._type, colDef?._subType);
  });

/** Derives input filter maps, letting each parameter's own type decide. */
export const deriveInputFilterMaps = (specs) => deriveFilterMaps(specs);

const computePendingChangesFromEntry = (
  entry,
  initialColumnsData,
  columnDisplayNameMap,
  t
) => {
  if (!entry) return [];
  const now = new Date();
  const result = [];

  const activeFilters = (entry.filters ?? []).filter(
    (f) => f._status !== "reverted"
  );
  const currentSpecFilters = activeFilters.map((f) => f._spec);
  const filterMaps = deriveFilterMapsFromSpec(
    currentSpecFilters,
    initialColumnsData
  );
  const savedFilterMaps = deriveFilterMapsFromSpec(
    (entry.savedSpecification?.filters ?? []).filter(
      (f) => f.function !== "SEARCH"
    ),
    initialColumnsData
  );
  const inputFilterMaps = deriveInputFilterMaps(
    activeFilterSpecs(entry.preAggregationFilters)
  );
  const savedInputFilterMaps = deriveInputFilterMaps(
    entry.savedSpecification?.preAggregationFilters ?? []
  );

  const columnChange = detectColumnChanges({
    columnOrder: entry.columnOrder,
    savedColumnOrder: entry.savedColumnOrder,
    pinnedColumns: entry.pinnedColumns,
    savedPinnedColumns: entry.savedPinnedColumns,
    columnVisibilityModel: entry.columnVisibilityModel,
    savedColumnVisibility: entry.savedColumnVisibility,
    now,
    t,
  });
  if (columnChange) result.push(columnChange);

  const sortChange = detectSortChanges({
    sortModel: entry.sortModel,
    savedSortModel: entry.savedSortModel,
    now,
    t,
  });
  if (sortChange) result.push(sortChange);

  const widthChange = detectColumnWidthChanges({
    columnWidths: entry.columnWidths,
    savedColumnWidths: entry.savedColumnWidths,
    now,
    t,
  });
  if (widthChange) result.push(widthChange);

  const decimalsChange = detectColumnDecimalsChanges({
    columnDecimals: entry.columnDecimals ?? {},
    savedColumnDecimals: entry.savedColumnDecimals ?? {},
    now,
    t,
  });
  if (decimalsChange) result.push(decimalsChange);

  result.push(
    ...detectAllFilterChanges({
      filterMaps,
      savedFilterMaps,
      inputFilterMaps,
      savedInputFilterMaps,
      columnDisplayNameMap: columnDisplayNameMap ?? {},
      now,
      t,
    })
  );

  const searchChange = detectSearchChanges({
    searchQuery: entry.searchQuery,
    savedSearchQuery: entry.savedSearchQuery,
    now,
    t,
  });
  if (searchChange) result.push(searchChange);

  const metricsChange = detectMetricsChanges({
    metricsItems: entry.metricsItems,
    savedMetricsItems: entry.savedMetricsItems,
    now,
    t,
  });
  if (metricsChange) result.push(metricsChange);

  result.push(
    ...detectRenameChanges({
      columnNameOverrides: entry.columnNameOverrides,
      columnDisplayNameMap: columnDisplayNameMap ?? {},
      now,
      t,
    })
  );

  const groupByChange = detectGroupByChanges({
    groupByColumns: entry.groupByColumns,
    savedGroupByColumns: entry.savedGroupByColumns,
    columnAggregations: entry.columnAggregations,
    savedColumnAggregations: entry.savedColumnAggregations,
    extraAggregateColumns: entry.extraAggregateColumns,
    savedExtraAggregateColumns: entry.savedExtraAggregateColumns,
    rangeGroupings: entry.rangeGroupings,
    savedRangeGroupings: entry.savedRangeGroupings,
    includeDetails: entry.includeDetails,
    savedIncludeDetails: entry.savedIncludeDetails,
    includeTotal: entry.includeTotal,
    savedIncludeTotal: entry.savedIncludeTotal,
    includeSubTotal: entry.includeSubTotal,
    savedIncludeSubTotal: entry.savedIncludeSubTotal,
    allValuesEnumerations: entry.allValuesEnumerations,
    savedAllValuesEnumerations: entry.savedAllValuesEnumerations,
    now,
    t,
  });
  if (groupByChange) result.push(groupByChange);

  const summaryChange = detectSummaryChanges({
    summaryType: entry.summaryType,
    summarySelections: entry.summarySelections,
    savedSummaryType: entry.savedSummaryType,
    savedSummarySelections: entry.savedSummarySelections,
    now,
    t,
  });
  if (summaryChange) result.push(summaryChange);

  return result.filter((c) => c.status === "pending");
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useReportState = (options) => {
  const {
    reportId,
    savedReport,
    sourceTables: sourceTablesProp,
    columnDisplayNameMap: columnDisplayNameMapProp,
    getColumnDataType,
    initialColumnsData,
    onReorderColumns,
    selectedResultIndex = 0,
  } = options;

  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  const sourceTables = useMemo(() => sourceTablesProp || ["review_list"], [
    sourceTablesProp,
  ]);

  const columnDisplayNameMap = useMemo(() => columnDisplayNameMapProp ?? {}, [
    columnDisplayNameMapProp,
  ]);

  // ---------------------------------------------------------------------------
  // Core state: single Map<tabIndex, TabEntry>
  // ---------------------------------------------------------------------------

  const [tabStates, setTabStates] = useState(() =>
    buildAllTabEntries(savedReport, sourceTablesProp || ["review_list"])
  );

  // Ref mirrors tabStates for use in callbacks that shouldn't re-run on tabStates changes
  const tabStatesRef = useRef(tabStates);
  tabStatesRef.current = tabStates;
  const pendingColumnOrderRef = useRef(null);
  pendingColumnOrderRef.current = null;

  // Stable ref to current tab index for setTabStates callbacks
  const selectedResultIndexRef = useRef(selectedResultIndex);
  selectedResultIndexRef.current = selectedResultIndex;

  // ---------------------------------------------------------------------------
  // dirtySheetChanges — kept separate from tabStates
  // ---------------------------------------------------------------------------

  const [dirtySheetChanges, setDirtySheetChanges] = useState(() => new Map());

  // ---------------------------------------------------------------------------
  // Current tab entry (lazy default if tab not yet in Map)
  // ---------------------------------------------------------------------------

  const currentTab = useMemo(() => {
    return (
      tabStates.get(selectedResultIndex) ??
      buildTabEntry(
        savedReport?.specification ?? null,
        savedReport?.configuration ?? null,
        sourceTables
      )
    );
  }, [tabStates, selectedResultIndex, savedReport, sourceTables]);

  // ---------------------------------------------------------------------------
  // Stable updater for current tab
  // ---------------------------------------------------------------------------

  const updateCurrentTab = useCallback(
    (updater) => {
      setTabStates((prev) => {
        const idx = selectedResultIndexRef.current;
        const entry = prev.get(idx) ?? buildTabEntry(null, null, sourceTables);
        const next = new Map(prev);
        next.set(idx, { ...entry, ...updater(entry) });
        return next;
      });
    },
    [sourceTables]
  );

  // ---------------------------------------------------------------------------
  // Reset on report change
  // ---------------------------------------------------------------------------

  const specFingerprint = useMemo(() => computeReportFingerprint(savedReport), [
    savedReport,
  ]);

  const lastResetReportNameRef = useRef(savedReport?.internalName);
  useEffect(() => {
    const sameReport =
      lastResetReportNameRef.current === savedReport?.internalName;
    lastResetReportNameRef.current = savedReport?.internalName;
    const rebuilt = buildAllTabEntries(savedReport, sourceTables);
    setTabStates((prev) =>
      sameReport ? carryOverConfigurationState(prev, rebuilt) : rebuilt
    );
    setDirtySheetChanges(new Map());
  }, [savedReport?.internalName, specFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Tab-switch: update dirtySheetChanges (no stale closure needed — tabStates is a dep)
  // ---------------------------------------------------------------------------

  const prevTabRef = useRef(selectedResultIndex);
  // useLayoutEffect fires before the browser paints, so the dirty-changes Map
  // is always up-to-date within the same paint cycle as the tab switch —
  // eliminating the one-frame flicker that useEffect would produce.
  // tabStates is intentionally omitted from deps: it is always current in the
  // render that triggers this (selectedResultIndex changed, tabStates did not).
  useLayoutEffect(() => {
    const prev = prevTabRef.current;
    if (prev === selectedResultIndex) return;
    prevTabRef.current = selectedResultIndex;

    const outgoing = tabStates.get(prev);
    setDirtySheetChanges((existing) => {
      const next = new Map(existing);
      if (outgoing) {
        const changes = computePendingChangesFromEntry(
          outgoing,
          initialColumnsData,
          columnDisplayNameMap,
          translateLabels
        );
        if (changes.length > 0) next.set(prev, changes);
        else next.delete(prev);
      }
      next.delete(selectedResultIndex);
      return next;
    });
  }, [selectedResultIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Destructure current tab
  // ---------------------------------------------------------------------------

  const {
    filters,
    preAggregationFilters,
    sortModel,
    columnVisibilityModel,
    columnOrder,
    pinnedColumns,
    columnWidths,
    columnDecimals,
    metricsItems,
    columnNameOverrides,
    groupByColumns,
    columnAggregations,
    extraAggregateColumns,
    removedExtraAggregateColumns,
    rangeGroupings,
    searchQuery,
    includeDetails,
    includeTotal,
    includeSubTotal,
    allValuesEnumerations,
    summarySelections,
    summaryType,
    baseSpecification,
    savedSortModel,
    savedColumnVisibility,
    savedColumnOrder,
    savedPinnedColumns,
    savedColumnWidths,
    savedColumnDecimals,
    savedMetricsItems,
    savedSearchQuery,
    savedGroupByColumns,
    savedColumnAggregations,
    savedExtraAggregateColumns,
    savedRangeGroupings,
    savedIncludeDetails,
    savedIncludeTotal,
    savedIncludeSubTotal,
    savedAllValuesEnumerations,
    savedSummarySelections,
    savedSummaryType,
    savedSpecification,
  } = currentTab;

  // ---------------------------------------------------------------------------
  // Column order sync from server columns (after data loads)
  // ---------------------------------------------------------------------------

  const deriveOrderFromColumnsData = useCallback(() => {
    return (initialColumnsData ?? []).map((c) => c.column);
  }, [initialColumnsData]);

  // useLayoutEffect so the correction happens before paint — prevents a brief
  // false-positive "columns changed" detection when the server result arrives.
  // savedColumnOrder is also updated because this sync reflects server reality,
  // not a user-made change, so both sides should remain equal.
  useLayoutEffect(() => {
    const serverCols = deriveOrderFromColumnsData().filter(
      (col) => !removedExtraAggregateColumns?.has(col)
    );
    if (serverCols.length === 0) return;
    const extraAgColSet = new Set([
      ...extraAggregateColumns.map((col) => col.column),
      ...Object.keys(rangeGroupings ?? {}).map(buildGroupingColumnName),
    ]);
    serverCols.forEach((col) => {
      if (extraAgColSet.has(col)) extraAgColSet.delete(col);
    });
    const differ = (a, b) => JSON.stringify(a) !== JSON.stringify(b);
    const colOrderNoExtras = columnOrder.filter((c) => !extraAgColSet.has(c));
    if (differ(colOrderNoExtras, serverCols)) {
      const missingEntries = [];
      for (var i = 0; i < columnOrder.length; i++) {
        if (extraAgColSet.has(columnOrder[i])) missingEntries.push(i);
      }
      missingEntries.forEach((i) => serverCols.splice(i, 0, columnOrder[i]));
      updateCurrentTab(() => ({
        columnOrder: serverCols,
        savedColumnOrder: serverCols,
      }));
    }
  }, [
    columnOrder,
    deriveOrderFromColumnsData,
    extraAggregateColumns,
    removedExtraAggregateColumns,
    rangeGroupings,
    updateCurrentTab,
  ]);

  // Column visibility sync from server columns (after data loads)
  // The result set is the source of truth for what columns exist and their
  // default visibility (notForExport). Saved spec overrides are applied on top,
  // then any current unsaved user changes are preserved.
  useLayoutEffect(() => {
    if (!initialColumnsData || initialColumnsData.length === 0) return;
    updateCurrentTab((entry) => {
      const newSaved = deriveVisibilityFromResultColumns(
        initialColumnsData,
        entry.savedSpecification
      );
      const newWorking = { ...newSaved };
      Object.keys(entry.columnVisibilityModel).forEach((col) => {
        if (
          col in newSaved &&
          entry.columnVisibilityModel[col] !== entry.savedColumnVisibility[col]
        ) {
          newWorking[col] = entry.columnVisibilityModel[col];
        }
      });
      return {
        columnVisibilityModel: newWorking,
        savedColumnVisibility: newSaved,
      };
    });
  }, [initialColumnsData, updateCurrentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const activeFilters = useMemo(
    () => filters.filter((f) => f._status !== "reverted"),
    [filters]
  );

  const activeInputFilters = useMemo(
    () => (preAggregationFilters ?? []).filter((f) => f._status !== "reverted"),
    [preAggregationFilters]
  );

  const specification = useMemo(
    () => ({
      ...baseSpecification,
      filters: activeFilters.map(({ _spec }) => _spec),
      preAggregationFilters: activeInputFilters.map(({ _spec }) => _spec),
    }),
    [baseSpecification, activeFilters, activeInputFilters]
  );

  const deriveTabFilterMaps = useCallback(
    (filtersToDerive) =>
      deriveFilterMapsFromSpec(filtersToDerive, initialColumnsData),
    [initialColumnsData]
  );

  const filterMaps = useMemo(() => deriveTabFilterMaps(specification.filters), [
    specification.filters,
    deriveTabFilterMaps,
  ]);

  const savedFilterMaps = useMemo(
    () =>
      deriveTabFilterMaps(
        (savedSpecification.filters ?? []).filter(
          (f) => f.function !== "SEARCH"
        )
      ),
    [savedSpecification.filters, deriveTabFilterMaps]
  );

  const inputFilterMaps = useMemo(
    () => deriveInputFilterMaps(specification.preAggregationFilters),
    [specification.preAggregationFilters]
  );

  const savedInputFilterMaps = useMemo(
    () => deriveInputFilterMaps(savedSpecification.preAggregationFilters ?? []),
    [savedSpecification.preAggregationFilters]
  );

  // ---------------------------------------------------------------------------
  // Change detection
  // ---------------------------------------------------------------------------

  const trackedChanges = useMemo(() => {
    const now = new Date();
    const result = [];

    const columnChange = detectColumnChanges({
      columnOrder,
      savedColumnOrder,
      pinnedColumns,
      savedPinnedColumns,
      columnVisibilityModel,
      savedColumnVisibility,
      now,
      t: translateLabels,
    });
    if (columnChange) result.push(columnChange);

    const sortChange = detectSortChanges({
      sortModel,
      savedSortModel,
      now,
      t: translateLabels,
    });
    if (sortChange) result.push(sortChange);

    const widthChange = detectColumnWidthChanges({
      columnWidths,
      savedColumnWidths,
      now,
      t: translateLabels,
    });
    if (widthChange) result.push(widthChange);

    const decimalsChange = detectColumnDecimalsChanges({
      columnDecimals: columnDecimals ?? {},
      savedColumnDecimals: savedColumnDecimals ?? {},
      now,
      t: translateLabels,
    });
    if (decimalsChange) result.push(decimalsChange);

    result.push(
      ...detectAllFilterChanges({
        filterMaps,
        savedFilterMaps,
        inputFilterMaps,
        savedInputFilterMaps,
        columnDisplayNameMap,
        now,
        t: translateLabels,
      })
    );

    const searchChange = detectSearchChanges({
      searchQuery,
      savedSearchQuery,
      now,
      t: translateLabels,
    });
    if (searchChange) result.push(searchChange);

    const metricsChange = detectMetricsChanges({
      metricsItems,
      savedMetricsItems,
      now,
      t: translateLabels,
    });
    if (metricsChange) result.push(metricsChange);

    result.push(
      ...detectRenameChanges({
        columnNameOverrides,
        columnDisplayNameMap,
        now,
        t: translateLabels,
      })
    );

    const groupByChange = detectGroupByChanges({
      groupByColumns,
      savedGroupByColumns,
      columnAggregations,
      savedColumnAggregations,
      extraAggregateColumns,
      savedExtraAggregateColumns,
      rangeGroupings,
      savedRangeGroupings,
      includeDetails,
      savedIncludeDetails,
      includeTotal,
      savedIncludeTotal,
      includeSubTotal,
      savedIncludeSubTotal,
      allValuesEnumerations,
      savedAllValuesEnumerations,
      now,
      t: translateLabels,
    });
    if (groupByChange) result.push(groupByChange);

    const summaryChange = detectSummaryChanges({
      summaryType,
      summarySelections,
      savedSummaryType,
      savedSummarySelections,
      now,
      t: translateLabels,
    });
    if (summaryChange) result.push(summaryChange);

    return result;
  }, [
    columnOrder,
    savedColumnOrder,
    pinnedColumns,
    savedPinnedColumns,
    columnVisibilityModel,
    savedColumnVisibility,
    sortModel,
    savedSortModel,
    columnWidths,
    savedColumnWidths,
    columnDecimals,
    savedColumnDecimals,
    filterMaps,
    savedFilterMaps,
    inputFilterMaps,
    savedInputFilterMaps,
    columnDisplayNameMap,
    searchQuery,
    savedSearchQuery,
    metricsItems,
    savedMetricsItems,
    columnNameOverrides,
    groupByColumns,
    savedGroupByColumns,
    columnAggregations,
    savedColumnAggregations,
    extraAggregateColumns,
    savedExtraAggregateColumns,
    rangeGroupings,
    savedRangeGroupings,
    includeDetails,
    savedIncludeDetails,
    includeTotal,
    savedIncludeTotal,
    includeSubTotal,
    savedIncludeSubTotal,
    allValuesEnumerations,
    savedAllValuesEnumerations,
    summaryType,
    summarySelections,
    savedSummaryType,
    savedSummarySelections,
    translateLabels,
  ]);

  const pendingTrackedChanges = useMemo(
    () => trackedChanges.filter((c) => c.status === "pending"),
    [trackedChanges]
  );
  const hasUnsavedChanges = pendingTrackedChanges.length > 0;

  // ---------------------------------------------------------------------------
  // Filter setters
  // ---------------------------------------------------------------------------

  const setFilters = useCallback(
    (updater) => {
      updateCurrentTab((entry) => ({
        filters:
          typeof updater === "function" ? updater(entry.filters) : updater,
      }));
    },
    [updateCurrentTab]
  );

  const setInputFilters = useCallback(
    (updater) => {
      updateCurrentTab((entry) => ({
        preAggregationFilters:
          typeof updater === "function"
            ? updater(entry.preAggregationFilters ?? [])
            : updater,
      }));
    },
    [updateCurrentTab]
  );

  const setFilter = useCallback(
    (column, filter, kind = FILTER_KIND_OUTPUT) => {
      const setList = kind === FILTER_KIND_INPUT ? setInputFilters : setFilters;
      setList((prev) => {
        const otherFilters = prev.filter((f) => f._spec.column !== column);
        if (!filter) return otherFilters;
        const newFilters = Array.isArray(filter) ? filter : [filter];
        return [...otherFilters, ...newFilters];
      });
    },
    [setFilters, setInputFilters]
  );

  const setValueFilter = useCallback(
    (column, values, kind = FILTER_KIND_OUTPUT) => {
      const newFilters =
        values && values.size > 0
          ? buildValueFilter(column, values, "applied")
          : null;
      setFilter(column, newFilters, kind);
    },
    [setFilter]
  );

  const setBooleanFilter = useCallback(
    (column, value, kind = FILTER_KIND_OUTPUT) => {
      const newFilters = value
        ? buildBooleanFilter(column, value, "applied")
        : null;
      setFilter(column, newFilters, kind);
    },
    [setFilter]
  );

  const setDateFilter = useCallback(
    (column, range, kind = FILTER_KIND_OUTPUT) => {
      const newFilters = isDateRangeSet(range)
        ? buildDateFilter(column, range, "applied")
        : null;
      setFilter(column, newFilters, kind);
    },
    [setFilter]
  );

  const setNumberFilter = useCallback(
    (column, range, kind = FILTER_KIND_OUTPUT) => {
      const newFilters =
        range && (range.min !== null || range.max !== null)
          ? buildNumberFilter(column, range, "applied")
          : null;
      setFilter(column, newFilters, kind);
    },
    [setFilter]
  );

  const clearFilter = useCallback(
    (column, kind = FILTER_KIND_OUTPUT) => {
      setFilter(column, null, kind);
    },
    [setFilter]
  );

  const clearAllFilters = useCallback(() => {
    updateCurrentTab(() => ({ filters: [] }));
  }, [updateCurrentTab]);

  const mapsForKind = useCallback(
    (kind) => (kind === FILTER_KIND_INPUT ? inputFilterMaps : filterMaps),
    [inputFilterMaps, filterMaps]
  );

  const clearFromDate = useCallback(
    (column, kind = FILTER_KIND_OUTPUT) => {
      const existing = mapsForKind(kind).date[column];
      if (!existing) return;
      if (existing.to === null) {
        setFilter(column, null, kind);
      } else {
        setDateFilter(column, { from: null, to: existing.to }, kind);
      }
    },
    [mapsForKind, setFilter, setDateFilter]
  );

  const clearToDate = useCallback(
    (column, kind = FILTER_KIND_OUTPUT) => {
      const existing = mapsForKind(kind).date[column];
      if (!existing) return;
      if (existing.from === null) {
        setFilter(column, null, kind);
      } else {
        setDateFilter(column, { from: existing.from, to: null }, kind);
      }
    },
    [mapsForKind, setFilter, setDateFilter]
  );

  const clearNumberMin = useCallback(
    (column, kind = FILTER_KIND_OUTPUT) => {
      const existing = mapsForKind(kind).number[column];
      if (!existing) return;
      if (existing.max === null) {
        setFilter(column, null, kind);
      } else {
        setNumberFilter(column, { min: null, max: existing.max }, kind);
      }
    },
    [mapsForKind, setFilter, setNumberFilter]
  );

  const clearNumberMax = useCallback(
    (column, kind = FILTER_KIND_OUTPUT) => {
      const existing = mapsForKind(kind).number[column];
      if (!existing) return;
      if (existing.min === null) {
        setFilter(column, null, kind);
      } else {
        setNumberFilter(column, { min: existing.min, max: null }, kind);
      }
    },
    [mapsForKind, setFilter, setNumberFilter]
  );

  const toggleFilterValue = useCallback(
    (column, value) => {
      const existing = filterMaps.value[column] ?? new Set();
      const newSet = new Set(existing);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      if (newSet.size === 0) {
        setFilter(column, null);
      } else {
        setValueFilter(column, newSet);
      }
    },
    [filterMaps.value, setFilter, setValueFilter]
  );

  const isValueSelected = useCallback(
    (column, value) => {
      return filterMaps.value[column]?.has(value) ?? false;
    },
    [filterMaps.value]
  );

  // ---------------------------------------------------------------------------
  // Other setters
  // ---------------------------------------------------------------------------

  const setSortModel = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        sortModel: typeof val === "function" ? val(entry.sortModel) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setColumnVisibilityModel = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        columnVisibilityModel:
          typeof val === "function" ? val(entry.columnVisibilityModel) : val,
      }));
    },
    [updateCurrentTab]
  );

  // onReorderColumns updates the parent's state, so it must be called outside
  // the setTabStates updater (which React may run while rendering this
  // provider). pendingColumnOrderRef carries the order between calls made in
  // the same batch, before tabStatesRef catches up on the next render.
  const setColumnOrderWithCallback = useCallback(
    (next) => {
      const idx = selectedResultIndexRef.current;
      const currentOrder =
        pendingColumnOrderRef.current?.idx === idx
          ? pendingColumnOrderRef.current.order
          : tabStatesRef.current.get(idx)?.columnOrder ?? [];
      const nextOrder = typeof next === "function" ? next(currentOrder) : next;
      pendingColumnOrderRef.current = { idx, order: nextOrder };
      setTabStates((prev) => {
        const nextMap = new Map(prev);
        nextMap.set(idx, { ...prev.get(idx), columnOrder: nextOrder });
        return nextMap;
      });
      onReorderColumns?.(nextOrder);
    },
    [onReorderColumns]
  );

  const setPinnedColumns = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        pinnedColumns:
          typeof val === "function" ? val(entry.pinnedColumns) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setColumnWidths = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        columnWidths: typeof val === "function" ? val(entry.columnWidths) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setColumnDecimals = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        columnDecimals:
          typeof val === "function" ? val(entry.columnDecimals) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setMetricsItems = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        metricsItems: typeof val === "function" ? val(entry.metricsItems) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setColumnNameOverrides = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        columnNameOverrides:
          typeof val === "function" ? val(entry.columnNameOverrides) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setGroupByColumns = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        groupByColumns:
          typeof val === "function" ? val(entry.groupByColumns) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setAllValuesEnumerations = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        allValuesEnumerations:
          typeof val === "function" ? val(entry.allValuesEnumerations) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setColumnAggregations = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        columnAggregations:
          typeof val === "function" ? val(entry.columnAggregations) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setExtraAggregateColumns = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        extraAggregateColumns:
          typeof val === "function" ? val(entry.extraAggregateColumns) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setRemovedExtraAggregateColumns = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        removedExtraAggregateColumns:
          typeof val === "function"
            ? val(entry.removedExtraAggregateColumns ?? new Set())
            : val,
      }));
    },
    [updateCurrentTab]
  );

  const setRangeGroupings = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        rangeGroupings:
          typeof val === "function" ? val(entry.rangeGroupings) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setIncludeDetails = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        includeDetails:
          typeof val === "function" ? val(entry.includeDetails) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setIncludeTotal = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        includeTotal: typeof val === "function" ? val(entry.includeTotal) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setIncludeSubTotal = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        includeSubTotal:
          typeof val === "function" ? val(entry.includeSubTotal) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setSearchQuery = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        searchQuery: typeof val === "function" ? val(entry.searchQuery) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setSummarySelections = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        summarySelections:
          typeof val === "function" ? val(entry.summarySelections) : val,
      }));
    },
    [updateCurrentTab]
  );

  const setSummaryType = useCallback(
    (val) => {
      updateCurrentTab((entry) => ({
        summaryType: typeof val === "function" ? val(entry.summaryType) : val,
      }));
    },
    [updateCurrentTab]
  );

  // ---------------------------------------------------------------------------
  // Revert handlers
  // ---------------------------------------------------------------------------

  const handleRevertChange = useCallback(
    (change) => {
      const { type, column } = change;
      const savedMapsForKind = (kind) =>
        kind === FILTER_KIND_INPUT ? savedInputFilterMaps : savedFilterMaps;

      if (type === "columns") {
        setColumnOrderWithCallback(savedColumnOrder);
        setColumnVisibilityModel(savedColumnVisibility);
        setPinnedColumns(savedPinnedColumns);
      } else if (type === "sortOrder") {
        setSortModel(savedSortModel);
      } else if (type === "columnWidths") {
        setColumnWidths(savedColumnWidths);
      } else if (type === "columnDecimals") {
        setColumnDecimals({ ...savedColumnDecimals });
      } else if (
        (type === "columnFilter" || type === INPUT_COLUMN_FILTER_CHANGE_TYPE) &&
        column
      ) {
        const kind = filterKindForChangeType(type);
        const saved = savedMapsForKind(kind).value[column];
        if (saved && saved.size > 0) {
          setValueFilter(column, new Set(saved), kind);
        } else {
          clearFilter(column, kind);
        }
      } else if (
        (type === "dateFilter" || type === INPUT_DATE_FILTER_CHANGE_TYPE) &&
        column
      ) {
        const kind = filterKindForChangeType(type);
        const saved = savedMapsForKind(kind).date[column];
        if (isDateRangeSet(saved)) {
          setDateFilter(
            column,
            {
              from: saved.from ?? null,
              to: saved.to ?? null,
              ...(saved.dynamic ? { dynamic: saved.dynamic } : {}),
            },
            kind
          );
        } else {
          clearFilter(column, kind);
        }
      } else if (
        (type === "numberFilter" || type === INPUT_NUMBER_FILTER_CHANGE_TYPE) &&
        column
      ) {
        const kind = filterKindForChangeType(type);
        const saved = savedMapsForKind(kind).number[column];
        if (saved && (saved.min !== null || saved.max !== null)) {
          setNumberFilter(
            column,
            { min: saved.min ?? null, max: saved.max ?? null },
            kind
          );
        } else {
          clearFilter(column, kind);
        }
      } else if (type === "search") {
        setSearchQuery(savedSearchQuery);
      } else if (type === "metrics") {
        setMetricsItems(savedMetricsItems);
      } else if (type === "rename" && column) {
        setColumnNameOverrides((prev) => {
          const next = { ...prev };
          delete next[column];
          return next;
        });
      } else if (type === "groupBy") {
        setGroupByColumns(savedGroupByColumns);
        setColumnAggregations(savedColumnAggregations);
        setExtraAggregateColumns(savedExtraAggregateColumns);
        setRemovedExtraAggregateColumns(new Set());
        setRangeGroupings(savedRangeGroupings);
        setAllValuesEnumerations(savedAllValuesEnumerations);
        setFilters((current) =>
          withoutOrphanedGroupingFilters(current, savedRangeGroupings)
        );
      } else if (type === "summary") {
        setSummarySelections({ ...savedSummarySelections });
        setSummaryType(savedSummaryType);
      }
    },
    [
      savedColumnOrder,
      savedColumnVisibility,
      savedPinnedColumns,
      savedSortModel,
      savedColumnWidths,
      savedColumnDecimals,
      savedFilterMaps,
      savedInputFilterMaps,
      savedSearchQuery,
      savedMetricsItems,
      setColumnOrderWithCallback,
      setValueFilter,
      setDateFilter,
      setNumberFilter,
      clearFilter,
      savedGroupByColumns,
      savedColumnAggregations,
      savedExtraAggregateColumns,
      savedRangeGroupings,
      savedAllValuesEnumerations,
      savedSummarySelections,
      savedSummaryType,
      setSummarySelections,
      setSummaryType,
      setSortModel,
      setColumnVisibilityModel,
      setPinnedColumns,
      setColumnWidths,
      setColumnDecimals,
      setSearchQuery,
      setMetricsItems,
      setColumnNameOverrides,
      setGroupByColumns,
      setColumnAggregations,
      setExtraAggregateColumns,
      setRemovedExtraAggregateColumns,
      setRangeGroupings,
      setFilters,
      setAllValuesEnumerations,
    ]
  );

  const handleRevertAllChanges = useCallback(() => {
    updateCurrentTab((entry) => ({
      columnNameOverrides: {},
      filters: toFrontendFilters(
        (entry.savedSpecification.filters ?? []).filter(
          (f) => f.function !== "SEARCH"
        ),
        "saved"
      ),
      preAggregationFilters: toFrontendFilters(
        entry.savedSpecification.preAggregationFilters ?? [],
        "saved"
      ),
      columnOrder: entry.savedColumnOrder,
      columnVisibilityModel: entry.savedColumnVisibility,
      pinnedColumns: entry.savedPinnedColumns,
      sortModel: entry.savedSortModel,
      columnWidths: entry.savedColumnWidths,
      columnDecimals: { ...entry.savedColumnDecimals },
      searchQuery: entry.savedSearchQuery,
      metricsItems: entry.savedMetricsItems,
      groupByColumns: entry.savedGroupByColumns,
      columnAggregations: entry.savedColumnAggregations,
      extraAggregateColumns: entry.savedExtraAggregateColumns,
      removedExtraAggregateColumns: new Set(),
      rangeGroupings: entry.savedRangeGroupings,
      includeDetails: entry.savedIncludeDetails,
      includeTotal: entry.savedIncludeTotal,
      includeSubTotal: entry.savedIncludeSubTotal,
      allValuesEnumerations: new Set(entry.savedAllValuesEnumerations),
      summarySelections: { ...entry.savedSummarySelections },
      summaryType: entry.savedSummaryType,
    }));
    onReorderColumns?.(savedColumnOrder);
  }, [updateCurrentTab, onReorderColumns, savedColumnOrder]);

  // ---------------------------------------------------------------------------
  // applyConfigurationPayload — initialise all tabs from a loaded config object
  // ---------------------------------------------------------------------------

  const applyConfigurationPayload = useCallback((payload, savedReport) => {
    if (!payload || !savedReport) return;
    setTabStates((prev) => {
      const next = new Map(prev);
      const applyToTab = (idx, internalName) => {
        const tabConfig = payload[internalName];
        if (!tabConfig) return;
        const entry = next.get(idx);
        if (!entry) return;
        const widths =
          tabConfig.widths && Object.keys(tabConfig.widths).length > 0
            ? tabConfig.widths
            : entry.columnWidths;
        const metrics =
          tabConfig.metrics?.length > 0
            ? tabConfig.metrics
            : entry.metricsItems;
        next.set(idx, {
          ...entry,
          columnWidths: widths,
          savedColumnWidths: widths,
          metricsItems: metrics,
          savedMetricsItems: metrics,
        });
      };
      if (savedReport.reports) {
        let outputIdx = 0;
        (savedReport.reports ?? []).forEach((subReport) => {
          if (subReport.notForOutput) return;
          applyToTab(outputIdx++, subReport.internalName);
        });
      } else {
        applyToTab(0, savedReport.internalName);
      }
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // markAsSaved
  // ---------------------------------------------------------------------------

  const markAsSaved = useCallback((savedSpec) => {
    setTabStates((prev) => {
      const idx = selectedResultIndexRef.current;
      const entry = prev.get(idx);
      if (!entry) return prev;
      const activeFiltersForSave = entry.filters.filter(
        (f) => f._status !== "reverted"
      );
      const activeInputFiltersForSave = (
        entry.preAggregationFilters ?? []
      ).filter((f) => f._status !== "reverted");
      const spec = {
        ...entry.baseSpecification,
        filters: activeFiltersForSave.map((f) => f._spec),
        preAggregationFilters: activeInputFiltersForSave.map((f) => f._spec),
        ...(savedSpec?.outputColumns && {
          outputColumns: savedSpec.outputColumns,
        }),
      };
      const next = new Map(prev);
      next.set(idx, {
        ...entry,
        filters: activeFiltersForSave.map((f) => ({ ...f, _status: "saved" })),
        preAggregationFilters: activeInputFiltersForSave.map((f) => ({
          ...f,
          _status: "saved",
        })),
        columnNameOverrides: {},
        savedSortModel: entry.sortModel,
        savedColumnVisibility: entry.columnVisibilityModel,
        savedColumnOrder: entry.columnOrder,
        savedPinnedColumns: entry.pinnedColumns,
        savedColumnWidths: entry.columnWidths,
        savedColumnDecimals: { ...entry.columnDecimals },
        savedMetricsItems: entry.metricsItems,
        savedSearchQuery: entry.searchQuery,
        savedGroupByColumns: new Set(entry.groupByColumns),
        savedColumnAggregations: { ...entry.columnAggregations },
        savedExtraAggregateColumns: [...entry.extraAggregateColumns],
        savedRangeGroupings: { ...entry.rangeGroupings },
        savedIncludeDetails: entry.includeDetails,
        savedIncludeTotal: entry.includeTotal,
        savedIncludeSubTotal: entry.includeSubTotal,
        savedAllValuesEnumerations: new Set(entry.allValuesEnumerations),
        savedSummarySelections: { ...entry.summarySelections },
        savedSummaryType: entry.summaryType,
        savedSpecification: spec,
      });
      return next;
    });
    setDirtySheetChanges(new Map());
  }, []);

  // ---------------------------------------------------------------------------
  // discardAll
  // ---------------------------------------------------------------------------

  const discardAll = useCallback(() => {
    updateCurrentTab((entry) => {
      const {
        filters: _f,
        preAggregationFilters: _p,
        ...savedBase
      } = entry.savedSpecification;
      return {
        filters: toFrontendFilters(
          (entry.savedSpecification.filters ?? []).filter(
            (f) => f.function !== "SEARCH"
          ),
          "saved"
        ),
        preAggregationFilters: toFrontendFilters(
          entry.savedSpecification.preAggregationFilters ?? [],
          "saved"
        ),
        baseSpecification: savedBase,
        sortModel: entry.savedSortModel,
        columnVisibilityModel: entry.savedColumnVisibility,
        pinnedColumns: entry.savedPinnedColumns,
        columnWidths: entry.savedColumnWidths,
        columnDecimals: { ...entry.savedColumnDecimals },
        searchQuery: entry.savedSearchQuery,
        metricsItems: entry.savedMetricsItems,
        columnNameOverrides: {},
        groupByColumns: entry.savedGroupByColumns,
        columnAggregations: entry.savedColumnAggregations,
        extraAggregateColumns: entry.savedExtraAggregateColumns,
        removedExtraAggregateColumns: new Set(),
        rangeGroupings: entry.savedRangeGroupings,
        includeDetails: entry.savedIncludeDetails,
        includeTotal: entry.savedIncludeTotal,
        includeSubTotal: entry.savedIncludeSubTotal,
        allValuesEnumerations: new Set(entry.savedAllValuesEnumerations),
        summarySelections: { ...entry.savedSummarySelections },
        summaryType: entry.savedSummaryType,
      };
    });
    onReorderColumns?.(savedColumnOrder);
  }, [updateCurrentTab, onReorderColumns, savedColumnOrder]);

  // ---------------------------------------------------------------------------
  // Column / filter helpers
  // ---------------------------------------------------------------------------

  const columnHandlers = useMemo(
    () => ({
      clearFilter,
      clearFromDate,
      clearToDate,
      clearNumberMin,
      clearNumberMax,
      toggleFilterValue,
      setDateRange: setDateFilter,
      setNumberRange: setNumberFilter,
      isValueSelected,
    }),
    [
      clearFilter,
      clearFromDate,
      clearToDate,
      clearNumberMin,
      clearNumberMax,
      toggleFilterValue,
      setDateFilter,
      setNumberFilter,
      isValueSelected,
    ]
  );

  const dateInputUtils = useMemo(
    () => ({ formatDateInput, parseDateInput }),
    []
  );

  const getActiveFilterGroups = useCallback(
    (options) => {
      const {
        groupingActive = false,
        getValueLabel,
        columnDisplayNameMap,
        dateFormat,
        formatDateValue,
        getDynamicDateLabel,
      } = options;
      const groups = new Map();
      const ensureChips = (target, kind) => {
        const column = filterDisplayColumn(target);
        if (!groups.has(column)) {
          groups.set(column, {
            [FILTER_KIND_INPUT]: [],
            [FILTER_KIND_OUTPUT]: [],
          });
        }
        return groups.get(column)[kind];
      };

      const addChips = (kind, maps) => {
        Object.entries(maps.value).forEach(([target, values]) => {
          if (values.size === 0) return;
          const { label, tooltip } = selectedValuesChipLabel(values, (value) =>
            getValueLabel?.(target, value)
          );
          ensureChips(target, kind).push({
            key: `${kind}-${target}-values`,
            label,
            tooltip,
            onDelete: () => clearFilter(target, kind),
          });
        });

        Object.entries(maps.date).forEach(([target, range]) => {
          if (!isDateRangeSet(range)) return;
          if (range.dynamic) {
            const { name } = range.dynamic;
            ensureChips(target, kind).push({
              key: `${kind}-${target}-dynamic`,
              label: getDynamicDateLabel ? getDynamicDateLabel(name) : name,
              onDelete: () => clearFilter(target, kind),
            });
            return;
          }
          if (range.from) {
            ensureChips(target, kind).push({
              key: `${kind}-${target}-from`,
              label: `from ${formatDateValue(range.from, dateFormat)}`,
              onDelete: () =>
                setDateFilter(target, { from: null, to: range.to }, kind),
            });
          }
          if (range.to) {
            ensureChips(target, kind).push({
              key: `${kind}-${target}-to`,
              label: `to ${formatDateValue(range.to, dateFormat)}`,
              onDelete: () =>
                setDateFilter(target, { from: range.from, to: null }, kind),
            });
          }
        });

        Object.entries(maps.number).forEach(([target, range]) => {
          if (range.min === null && range.max === null) return;
          const column = filterDisplayColumn(target);
          const fmt = columnFilterNumberFormatter(
            initialColumnsData,
            column,
            kind,
            getColumnDataType(column)
          );
          if (range.min !== null) {
            ensureChips(target, kind).push({
              key: `${kind}-${target}-min`,
              label: `min: ${fmt.short(range.min)}`,
              tooltip: fmt.exact(range.min),
              onDelete: () =>
                setNumberFilter(target, { min: null, max: range.max }, kind),
            });
          }
          if (range.max !== null) {
            ensureChips(target, kind).push({
              key: `${kind}-${target}-max`,
              label: `max: ${fmt.short(range.max)}`,
              tooltip: fmt.exact(range.max),
              onDelete: () =>
                setNumberFilter(target, { min: range.min, max: null }, kind),
            });
          }
        });
      };

      addChips(FILTER_KIND_INPUT, inputFilterMaps);
      addChips(FILTER_KIND_OUTPUT, filterMaps);

      const kindLabels = filterKindLabels(translateLabels, groupingActive);

      return Array.from(groups.entries())
        .map(([column, chipsByKind]) => ({
          column,
          columnLabel: columnDisplayNameMap[column] || column,
          sections: [FILTER_KIND_INPUT, FILTER_KIND_OUTPUT]
            .map((kind) => ({
              kind,
              kindLabel: kindLabels[kind],
              chips: chipsByKind[kind],
            }))
            .filter((section) => section.chips.length > 0),
        }))
        .filter((group) => group.sections.length > 0);
    },
    [
      filterMaps,
      inputFilterMaps,
      clearFilter,
      setDateFilter,
      getColumnDataType,
      initialColumnsData,
      setNumberFilter,
      translateLabels,
    ]
  );

  const getFiltersForColumn = useCallback(
    (column) => filters.filter((f) => f._spec.column === column),
    [filters]
  );

  const getActiveFiltersForColumn = useCallback(
    (column) => activeFilters.filter((f) => f._spec.column === column),
    [activeFilters]
  );

  const isColumnFiltered = useCallback(
    (column) => {
      const grouping = buildGroupingColumnName(column);
      return (
        activeFilters.some(
          (f) => f._spec.column === column || f._spec.column === grouping
        ) || activeInputFilters.some((f) => f._spec.column === column)
      );
    },
    [activeFilters, activeInputFilters]
  );

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    reportId,
    savedReport,
    sourceTables,
    specification,
    savedSpecification,

    hasUnsavedChanges,
    columnDisplayNameMap,

    filters,
    activeFilters,
    filterMaps,
    savedFilterMaps,
    preAggregationFilters,
    activeInputFilters,
    inputFilterMaps,
    savedInputFilterMaps,

    sortModel,
    setSortModel,
    savedSortModel,

    columnVisibilityModel,
    setColumnVisibilityModel,
    savedColumnVisibility,

    columnOrder,
    setColumnOrder: setColumnOrderWithCallback,
    savedColumnOrder,

    pinnedColumns,
    setPinnedColumns,
    savedPinnedColumns,

    columnWidths,
    setColumnWidths,
    savedColumnWidths,

    columnDecimals,
    setColumnDecimals,
    savedColumnDecimals,

    searchQuery,
    setSearchQuery,
    savedSearchQuery,

    metricsItems,
    setMetricsItems,
    savedMetricsItems,

    columnNameOverrides,
    setColumnNameOverrides,

    groupByColumns,
    setGroupByColumns,
    savedGroupByColumns,
    columnAggregations,
    setColumnAggregations,
    savedColumnAggregations,
    extraAggregateColumns,
    setExtraAggregateColumns,
    savedExtraAggregateColumns,
    removedExtraAggregateColumns,
    setRemovedExtraAggregateColumns,
    rangeGroupings,
    setRangeGroupings,
    savedRangeGroupings,
    includeDetails,
    setIncludeDetails,
    includeTotal,
    setIncludeTotal,
    includeSubTotal,
    setIncludeSubTotal,
    allValuesEnumerations,
    setAllValuesEnumerations,

    summarySelections,
    setSummarySelections,
    savedSummarySelections,
    summaryType,
    setSummaryType,
    savedSummaryType,

    trackedChanges,
    pendingTrackedChanges,

    handleRevertChange,
    handleRevertAllChanges,

    setFilter,
    setValueFilter,
    setBooleanFilter,
    setDateFilter,
    setNumberFilter,
    clearFilter,
    clearAllFilters,

    columnHandlers,
    dateInputUtils,

    applyConfigurationPayload,
    markAsSaved,
    discardAll,

    helpers: {
      getFiltersForColumn,
      getActiveFiltersForColumn,
      isColumnFiltered,
    },
    getActiveFilterGroups,

    selectedResultIndex,
    tabStatesRef,
    dirtySheets: new Set(dirtySheetChanges.keys()),
    dirtySheetChanges,
  };
};
